import { useForm, Control, UseFormMethods, SubmitHandler } from 'react-hook-form';

import { useParams } from 'react-router-dom';
import { GqlUpdateWebCustomizationSettingsV2Input, RadiusOption, ProductCardButtonOption } from 'types/graphql';

import useErnie from 'shared/hooks/use-ernie';
import { PreviewData } from '../components/preview';
import { useUpdateWebCustomizationSettingsV2, MenuCustomizationData } from '../data-access';
import { ThemeFormProps } from './theme.types';

type HandlePublish = ReturnType<UseFormMethods<FormValues>['handleSubmit']>;

type FormValues = {
  buttonsLinks: string;
  mobileBackgroundColor?: string;
  mobileHeaderBackgroundColor?: string;
  mobileHeaderImageUrl?: string;
  mobileHeaderImageUrlV2?: string;
  mobileNavigationBarActiveColor?: string;
  mobileNavigationBarBackgroundColor?: string;
  mobileNavigationBarInactiveColor?: string;
  mobileShowTabLabels?: boolean;
  mobileTabShowSpecials: boolean;
  mobileTabShowScanPay: boolean;
  mobileTabShowCart: boolean;
  mobileTabShowLoyalty: boolean;
  mobileTabLabelAccount?: string;
  mobileTabLabelCart?: string;
  mobileTabLabelLoyalty?: string;
  mobileTabLabelMore?: string;
  mobileTabLabelPay?: string;
  mobileTabLabelShop?: string;
  mobileTabLabelSpecials?: string;
  navBar: string;
  staffPickTag: string;
  discountTag: string;
  defaultRadius: RadiusOption;
  productCardButton?: ProductCardButtonOption;
};

type Input = Pick<GqlUpdateWebCustomizationSettingsV2Input, 'colors' | 'mobile' | 'radius'>;

type UseThemeFormReturn = {
  control: Control<FormValues>;
  handlePublish: HandlePublish;
  handleReset: () => void;
  isDirty: boolean;
  previewData: PreviewData;
  applyAllInput: Input;
  afterApplyAll: () => void;
};

function valueOrFallback(value: string | null | undefined, fallback: string): string {
  return value?.length ? value : fallback;
}

function useMergeDataWithFormValues(this: void, data: MenuCustomizationData, formValues: FormValues): PreviewData {
  const { buttonsLinks, navBar, staffPickTag, discountTag, defaultRadius, productCardButton } = formValues;

  return {
    ...data,
    colors: {
      buttonsLinks,
      navBar,
      staffPickTag,
      discountTag,
      productCardButton: productCardButton ?? ProductCardButtonOption.tinted,
    },
    mobile: {
      backgroundColor: valueOrFallback(formValues.mobileBackgroundColor, data.mobile?.backgroundColor ?? ''),
      headerBackgroundColor: valueOrFallback(
        formValues.mobileHeaderBackgroundColor,
        data.mobile?.headerBackgroundColor ?? ''
      ),
      headerImageUrl: valueOrFallback(formValues.mobileHeaderImageUrl, data.mobile?.headerImageUrl ?? ''),
      headerImageUrlV2: valueOrFallback(formValues.mobileHeaderImageUrlV2, data.mobile?.headerImageUrlV2 ?? ''),
      bottomBarActiveColor: valueOrFallback(
        formValues.mobileNavigationBarActiveColor,
        data.mobile?.bottomBarActiveColor ?? ''
      ),
      bottomBarBackgroundColor: valueOrFallback(
        formValues.mobileNavigationBarBackgroundColor,
        data.mobile?.bottomBarBackgroundColor ?? ''
      ),
      bottomBarInactiveColor: valueOrFallback(
        formValues.mobileNavigationBarInactiveColor,
        data.mobile?.bottomBarInactiveColor ?? ''
      ),
      showTabLabels: formValues.mobileShowTabLabels ?? false,
      tabDisplay: {
        showSpecials: formValues.mobileTabShowSpecials,
        showPay: formValues.mobileTabShowScanPay,
        showCart: formValues.mobileTabShowCart,
        showLoyalty: formValues.mobileTabShowLoyalty,
      },
      tabLabels: {
        account: valueOrFallback(formValues.mobileTabLabelAccount, data.mobile?.tabLabels?.account ?? ''),
        cart: valueOrFallback(formValues.mobileTabLabelCart, data.mobile?.tabLabels?.cart ?? ''),
        loyalty: valueOrFallback(formValues.mobileTabLabelLoyalty, data.mobile?.tabLabels?.loyalty ?? ''),
        more: valueOrFallback(formValues.mobileTabLabelMore, data.mobile?.tabLabels?.more ?? ''),
        pay: valueOrFallback(formValues.mobileTabLabelPay, data.mobile?.tabLabels?.pay ?? ''),
        shop: valueOrFallback(formValues.mobileTabLabelShop, data.mobile?.tabLabels?.shop ?? ''),
        specials: valueOrFallback(formValues.mobileTabLabelSpecials, data.mobile?.tabLabels?.specials ?? ''),
      },
    },
    radius: {
      default: defaultRadius,
    },
  };
}

function formatInput(values: FormValues): Input {
  return {
    colors: {
      buttonsLinks: values.buttonsLinks,
      navBar: values.navBar,
      staffPickTag: values.staffPickTag,
      discountTag: values.discountTag,
      productCardButton: values.productCardButton,
    },
    mobile: {
      backgroundColor: values.mobileBackgroundColor,
      headerBackgroundColor: values.mobileHeaderBackgroundColor,
      headerImageUrl: values.mobileHeaderImageUrl,
      headerImageUrlV2: values.mobileHeaderImageUrlV2,
      bottomBarActiveColor: values.mobileNavigationBarActiveColor,
      bottomBarBackgroundColor: values.mobileNavigationBarBackgroundColor,
      bottomBarInactiveColor: values.mobileNavigationBarInactiveColor,
      showTabLabels: values.mobileShowTabLabels,
      tabDisplay: {
        showSpecials: values.mobileTabShowSpecials,
        showPay: values.mobileTabShowScanPay,
        showCart: values.mobileTabShowCart,
        showLoyalty: values.mobileTabShowLoyalty,
      },
      tabLabels: {
        account: values.mobileTabLabelAccount,
        cart: values.mobileTabLabelCart,
        loyalty: values.mobileTabLabelLoyalty,
        more: values.mobileTabLabelMore,
        pay: values.mobileTabLabelPay,
        shop: values.mobileTabLabelShop,
        specials: values.mobileTabLabelSpecials,
      },
    },
    radius: {
      default: values.defaultRadius,
    },
  };
}

export function useThemeForm({ data }: ThemeFormProps): UseThemeFormReturn {
  const {
    colors: { buttonsLinks, navBar, staffPickTag, discountTag, productCardButton },
    mobile,
    radius: { default: defaultRadius },
  } = data;

  const defaultValues = {
    buttonsLinks,
    navBar,
    defaultRadius,
    mobileBackgroundColor: mobile?.backgroundColor ?? '',
    mobileHeaderBackgroundColor: mobile?.headerBackgroundColor ?? buttonsLinks,
    mobileHeaderImageUrl: mobile?.headerImageUrl ?? '',
    mobileHeaderImageUrlV2: mobile?.headerImageUrlV2 ?? '',
    mobileNavigationBarActiveColor: mobile?.bottomBarActiveColor ?? '',
    mobileNavigationBarBackgroundColor: mobile?.bottomBarBackgroundColor ?? '',
    mobileNavigationBarInactiveColor: mobile?.bottomBarInactiveColor ?? '',
    mobileShowTabLabels: mobile?.showTabLabels ?? false,
    mobileTabShowCart: mobile?.tabDisplay?.showCart ?? true,
    mobileTabShowScanPay: mobile?.tabDisplay?.showPay ?? true,
    mobileTabShowLoyalty: mobile?.tabDisplay?.showLoyalty ?? true,
    mobileTabShowSpecials: mobile?.tabDisplay?.showSpecials ?? false,
    mobileTabLabelAccount: mobile?.tabLabels?.account ?? '',
    mobileTabLabelCart: mobile?.tabLabels?.cart ?? '',
    mobileTabLabelLoyalty: mobile?.tabLabels?.loyalty ?? '',
    mobileTabLabelMore: mobile?.tabLabels?.more ?? '',
    mobileTabLabelShop: mobile?.tabLabels?.shop ?? '',
    mobileTabLabelSpecials: mobile?.tabLabels?.specials ?? '',
    mobileTabLabelPay: mobile?.tabLabels?.pay ?? '',
    // Need to account for flag being off
    staffPickTag: staffPickTag ?? '',
    discountTag: discountTag ?? '',
    productCardButton,
  };

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    watch,
  } = useForm<FormValues>({ defaultValues });

  const { id: dispensaryId } = useParams<{ id: string }>();

  const showErnie = useErnie();

  const updateData = useUpdateWebCustomizationSettingsV2();

  const onSubmit: SubmitHandler<FormValues> = async (submittedValues) => {
    const input = formatInput(submittedValues);

    try {
      await updateData({
        variables: {
          dispensaryId,
          input,
        },
      });

      showErnie('Your theme has been updated', 'success');
      reset({ ...submittedValues });
    } catch (err) {
      showErnie('Something went wrong, please try again.', 'danger');
      console.error(err);
    }
  };

  const handlePublish = handleSubmit(onSubmit);

  const handleReset = (): void => reset();

  const formValues = watch();

  const previewData = useMergeDataWithFormValues(data, formValues);

  const applyAllInput = formatInput(formValues);
  const afterApplyAll = (): void => {
    reset({ ...formValues });
  };

  return {
    control,
    handlePublish,
    handleReset,
    isDirty,
    previewData,
    applyAllInput,
    afterApplyAll,
  };
}
